import { lazy, memo, Suspense } from 'preact/compat';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  getPortalConsultantUsers,
  getPortalLocations,
  getPortalRecentQueries,
  searchPublishedListings,
} from '../lib/portal-api.js';
import {
  prefetchPortalDetailPageChunk,
  prefetchPortalDetailShell,
} from '../lib/portal-detail-api.js';
import { fetchListingCategories } from '../ilan-vitrin/api.js';
import { mapPortalQueryTypeToListingCategory } from '../lib/portal-listing-search-params.js';
import {
  buildListingSearchParams,
  listingSearchCacheKey,
  serializePortalFiltersForQuery,
  isLandCategoryFilters,
  normalizeQueryTypeCsv,
  normalizeSortBy,
  normalizeSortDir,
  PORTAL_LISTING_SORT_FIELDS,
  PORTAL_LIST_MODE,
  splitCsvParam,
  starsFilterValueFromMetaRange,
} from '../lib/portal-listing-filters-build.js';
import {
  formatArea,
  formatCurrency,
  formatDate,
  formatNavRouteKm,
  formatRoadFrontageMeters,
  formatTitleCaseTr,
  normalizeParcelShapeLabel,
  queryTypeLabel,
} from '../lib/formatters.js';
import { combinedPortalMetaScorePct, PortalMetaStars } from './PortalMetaStars.jsx';
import { listingDetailHref } from '../lib/listing-canonical-path.js';
import {
  buildPortalStarBlendItemFromListingRow,
  buildPortalStarBlendItemFromListRow,
  resolveListRowMetaStarsPct,
} from '../lib/portalListStarMeta.js';
import { formatListingInvestmentScore, PortalListingResultCard as ListingResultCard } from './PortalListingResultCard.jsx';
import { VitrinCategorySidebar } from '../ilan-vitrin/VitrinCategorySidebar.jsx';
import { PortalListingFiltersForm } from './PortalListingFiltersForm.jsx';
import { PortalRecentQueriesVirtualList } from './portal/PortalRecentQueriesVirtualList.jsx';
import { readPpCanAccessProsorgu, readPpCanAccessVault, usePpPortalAuthenticated } from '../lib/pp-user-auth.js';
import { isWebIlanPortalUiEnabled } from '../lib/web-ilan-portal-ui.js';
import {
  buildPortalCompareItemFromListingRow,
  buildPortalCompareItemFromQueryRow,
  getPortalComparePageHref,
  PORTAL_COMPARE_MAX_ITEMS,
  togglePortalCompareItem,
  usePortalCompareSelection,
} from '../lib/portal-compare-store.js';

const RatingsDrawer = lazy(() => import('./ResultRatingsDrawer.jsx').then((module) => ({
  default: module.ResultRatingsDrawer,
})));

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 50, 100, 200, 250];
const SORT_FIELDS = PORTAL_LISTING_SORT_FIELDS;
const PORTAL_RECENT_CITY_STORAGE_KEY = 'pp.portal.recentQueries.lastCityId';
const PORTAL_RECENT_QUERY_CACHE_PREFIX = 'pp.portal.recentQueries.cache.v2';

/** Liste başlığı — vitrin + sorgu tablosu yola cephe sütunu (Mt: metre) */
const PORTAL_LIST_ROAD_HEADER = 'YOLA CEPHE';

/** Sıralama başlığı + kart satırı (gm / investment score). CSS uppercase → MÜLK SKORU */
const PORTAL_LIST_INVESTMENT_SCORE_LABEL = 'Mülk Skoru';

function resolveRatingSuccessCount(item) {
  const asNum = (raw) => {
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };
  const direct = asNum(item?.rating_success_count) ?? asNum(item?.success_count) ?? asNum(item?.rating_summary?.success_count);
  if (direct != null) return Math.max(0, Math.round(direct));
  const count = asNum(item?.rating_count) ?? asNum(item?.rating_summary?.rating_count);
  const pct = asNum(item?.rating_score_pct) ?? asNum(item?.rating_summary?.rating_score_pct);
  if (count != null && pct != null) {
    return Math.max(0, Math.round((Math.max(0, count) * Math.min(100, Math.max(0, pct))) / 100));
  }
  return 0;
}

function getStoredPortalRecentCityId() {
  if (typeof window === 'undefined') return '';
  try {
    return String(window.localStorage.getItem(PORTAL_RECENT_CITY_STORAGE_KEY) || '').trim();
  } catch (_) {
    return '';
  }
}

function setStoredPortalRecentCityId(cityId) {
  if (typeof window === 'undefined') return;
  const normalized = String(cityId || '').trim();
  if (!normalized) return;
  try {
    window.localStorage.setItem(PORTAL_RECENT_CITY_STORAGE_KEY, normalized);
  } catch (_) {
    // localStorage erişilemiyorsa sessizce devam et.
  }
}

function getUrlCityId() {
  if (typeof window === 'undefined') return '';
  return String(new URLSearchParams(window.location.search).get('city_id') || '').trim();
}

function portalRecentQueryCacheKey(serializedFilters) {
  return `${PORTAL_RECENT_QUERY_CACHE_PREFIX}:${String(serializedFilters || '').trim()}`;
}

function readPortalRecentQueryCache(serializedFilters) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(portalRecentQueryCacheKey(serializedFilters));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!Array.isArray(parsed.results)) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function writePortalRecentQueryCache(serializedFilters, payload) {
  if (typeof window === 'undefined') return;
  if (!payload || typeof payload !== 'object') return;
  if (listHasPendingThumbnails(payload)) return;
  try {
    window.sessionStorage.setItem(
      portalRecentQueryCacheKey(serializedFilters),
      JSON.stringify(payload),
    );
  } catch (_) {
    // sessionStorage dolu/kapalı ise sessiz geç.
  }
}

function listHasPendingThumbnails(data) {
  return (data?.results || []).some(
    (row) => !row?.thumbnail_url || row?.thumbnail_status === 'pending',
  );
}

function pickDefaultCity(cities, preferredCityId = '') {
  const rows = Array.isArray(cities) ? cities : [];
  const preferred = String(preferredCityId || '').trim();
  if (preferred) {
    const matched = rows.find((city) => String(city?.id) === preferred);
    if (matched) return { city: matched, source: 'stored' };
  }
  if (!rows.length) return { city: null, source: '' };
  const topCity = rows.reduce((best, current) => {
    const currentCount = Number(current?.count) || 0;
    const bestCount = Number(best?.count) || 0;
    return currentCount > bestCount ? current : best;
  }, rows[0]);
  return { city: topCity || null, source: topCity ? 'popular' : '' };
}

const QUERY_TYPE_OPTIONS = [
  { value: 'arsa', label: 'Arsa' },
  { value: 'tarla', label: 'Tarla' },
  { value: 'villa', label: 'Villa' },
  { value: 'bina', label: 'Bina' },
  { value: 'mustakil_ev', label: 'Müstakil Ev' },
  { value: 'ciftlik_ev', label: 'Çiftlik Evi' },
  { value: 'fabrika', label: 'Fabrika' },
];

const LAND_INFRA_TRI_OPTIONS = [
  { value: '', label: 'Tümü' },
  { value: '1', label: 'Evet' },
  { value: '0', label: 'Hayır' },
];

const META_STAR_FILTER_OPTIONS = [
  { value: '', label: 'Tümü' },
  { value: '1', label: '1 yıldız ve altı' },
  { value: '2', label: '2 yıldız ve altı' },
  { value: '3', label: '3 yıldız ve altı' },
  { value: '4', label: '4 yıldız ve altı' },
  { value: '5', label: '5 yıldız ve altı' },
];

/** Şehir / ilçe merkezine rota (InDistanceM_*), üst sınır metre — backend max_nav_*_m */
const NAV_CITY_MAX_OPTIONS = [
  { value: '', label: 'Tümü' },
  { value: '5000', label: '≤ 5 km' },
  { value: '10000', label: '≤ 10 km' },
  { value: '20000', label: '≤ 20 km' },
  { value: '35000', label: '≤ 35 km' },
];

const NAV_TOWN_MAX_OPTIONS = [
  { value: '', label: 'Tümü' },
  { value: '3000', label: '≤ 3 km' },
  { value: '8000', label: '≤ 8 km' },
  { value: '15000', label: '≤ 15 km' },
];

function findLocationName(list, id) {
  if (id === '' || id == null) return '';
  const row = (list || []).find((x) => String(x.id) === String(id));
  return row?.name || '';
}

function preferQuarterRow(current, candidate) {
  const candKey = String(candidate?.list_key || '');
  const curKey = String(current?.list_key || '');
  if (candKey.includes(':p:') && !curKey.includes(':p:')) return candidate;
  if (curKey.includes(':p:') && !candKey.includes(':p:')) return current;
  const candName = String(candidate?.name || '').trim();
  const curName = String(current?.name || '').trim();
  if (candName.length > curName.length) return candidate;
  return current;
}

function locationSelectOptions(placeholder, rows) {
  const byId = new Map();
  for (const row of rows || []) {
    if (row?.id == null || row.id === '') continue;
    const id = String(row.id);
    const prev = byId.get(id);
    byId.set(id, prev ? preferQuarterRow(prev, row) : row);
  }
  return [
    { value: '', label: placeholder },
    ...[...byId.values()].map((row) => ({
      value: String(row.id),
      label: `${row.name} (${row.count})`,
    })),
  ];
}

function formatRangeChipPart(minV, maxV, unit) {
  const hasMin = minV !== '' && minV != null;
  const hasMax = maxV !== '' && maxV != null;
  if (!hasMin && !hasMax) return null;
  if (hasMin && hasMax) return `${minV} – ${maxV} ${unit}`;
  if (hasMin) return `≥ ${minV} ${unit}`;
  return `≤ ${maxV} ${unit}`;
}

function triInfraLabel(v) {
  const o = LAND_INFRA_TRI_OPTIONS.find((x) => String(x.value) === String(v));
  return o ? o.label : String(v);
}

function formatCategoryMainChip(main) {
  const raw = String(main || '').trim();
  if (!raw) return '';
  return formatTitleCaseTr(raw.replace(/_/g, ' ')) || raw;
}

function formatCategoryIdChip(id) {
  const raw = String(id || '').trim();
  if (!raw) return raw;
  return formatTitleCaseTr(raw.replace(/_/g, ' ')) || raw;
}

/** İl dışındaki tüm filtreleri sıfırlar; konum + liste modu + sıralama + sayfa boyutu korunur */
function getSecondaryFiltersClearPatch(f) {
  return {
    ...f,
    page: 1,
    query_type: '',
    expert_answered: '',
    price_advantage_only: '',
    area_m2_min: '',
    area_m2_max: '',
    unit_price_min: '',
    unit_price_max: '',
    total_price_min: '',
    total_price_max: '',
    gm_min: '',
    gm_max: '',
    quarter_score_min: '',
    quarter_score_max: '',
    meta_min: '',
    meta_max: '',
    puan_min: '',
    puan_max: '',
    listing_has_road: '',
    listing_has_water: '',
    listing_has_power_line: '',
    road_frontage_min_m: '',
    road_frontage_max_m: '',
    max_nav_city_m: '',
    max_nav_town_m: '',
    category_main: '',
    category_type: '',
    category_leaf_id: '',
    listing_attr: '',
    mine: false,
    town_ids: '',
    quarter_ids: '',
    is_sale: '',
    listing_type: '',
    aranacaklar_contact_id: '',
    aranacaklar_contact_name: '',
    consultant_user_id: '',
    consultant_user_name: '',
  };
}

/**
 * Uygulanan filtrelerden chip listesi (başlık üstü şerit).
 * @param {(patch: Record<string, unknown>) => void} applyPatch — page: 1 eklenir
 */
function buildPortalFilterChips(f, ctx) {
  const {
    cityName,
    townName,
    quarterName,
    cityRows,
    applyPatch,
  } = ctx;
  const chips = [];

  const add = (chip) => {
    chips.push(chip);
  };

  if (f.aranacaklar_contact_id) {
    add({
      id: 'aranacaklar-contact',
      isLocation: false,
      groupLabel: 'Müşteri Filtresi',
      valueText: f.aranacaklar_contact_name || `Kişi ${f.aranacaklar_contact_id}`,
      onRemove: () => applyPatch({
        aranacaklar_contact_id: '',
        aranacaklar_contact_name: '',
      }),
    });
  }

  if (f.consultant_user_id) {
    add({
      id: 'consultant-user',
      isLocation: false,
      groupLabel: 'Kullanıcı',
      valueText: f.consultant_user_name || `Kullanıcı #${f.consultant_user_id}`,
      onRemove: () => applyPatch({
        consultant_user_id: '',
        consultant_user_name: '',
      }),
    });
  }

  if (f.city_id) {
    add({
      id: 'loc-city',
      isLocation: true,
      groupLabel: 'İl',
      valueText: cityName || findLocationName(cityRows, f.city_id) || `İl #${f.city_id}`,
      onRemove: () => applyPatch({ city_id: '', town_id: '', quarter_id: '' }),
    });
  }
  if (f.town_id) {
    add({
      id: 'loc-town',
      isLocation: true,
      groupLabel: 'İlçe',
      valueText: townName || `İlçe #${f.town_id}`,
      onRemove: () => applyPatch({ town_id: '', quarter_id: '' }),
    });
  }
  if (f.quarter_id) {
    add({
      id: 'loc-quarter',
      isLocation: true,
      groupLabel: 'Mahalle',
      valueText: quarterName || `Mahalle #${f.quarter_id}`,
      onRemove: () => applyPatch({ quarter_id: '' }),
    });
  }

  normalizeQueryTypeCsv(f.query_type).forEach((qt) => {
    const opt = QUERY_TYPE_OPTIONS.find((o) => o.value === qt);
    add({
      id: `query-type-${qt}`,
      isLocation: false,
      groupLabel: 'Tür',
      valueText: opt ? opt.label : queryTypeLabel(qt),
      onRemove: () => applyPatch({
        query_type: normalizeQueryTypeCsv(f.query_type).filter((v) => v !== qt).join(','),
      }),
    });
  });

  if (f.expert_answered === '1') {
    add({
      id: 'expert',
      isLocation: false,
      groupLabel: 'Uzman',
      valueText: 'Yanıtlanmış uzman talebi',
      onRemove: () => applyPatch({ expert_answered: '' }),
    });
  }

  if (f.price_advantage_only === '1') {
    add({
      id: 'price-advantage',
      isLocation: false,
      groupLabel: 'Fiyat',
      valueText: 'Sadece fiyat avantajı',
      onRemove: () => applyPatch({ price_advantage_only: '' }),
    });
  }

  const areaTxt = formatRangeChipPart(f.area_m2_min, f.area_m2_max, 'm²');
  if (areaTxt) {
    add({
      id: 'area',
      isLocation: false,
      groupLabel: 'Alan',
      valueText: areaTxt,
      onRemove: () => applyPatch({ area_m2_min: '', area_m2_max: '' }),
    });
  }

  const roadTxt = formatRangeChipPart(f.road_frontage_min_m, f.road_frontage_max_m, 'm');
  if (roadTxt) {
    add({
      id: 'road-frontage',
      isLocation: false,
      groupLabel: 'Yola cephe',
      valueText: roadTxt,
      onRemove: () => applyPatch({ road_frontage_min_m: '', road_frontage_max_m: '' }),
    });
  }

  if (f.max_nav_city_m) {
    const opt = NAV_CITY_MAX_OPTIONS.find((o) => String(o.value) === String(f.max_nav_city_m));
    add({
      id: 'nav-city',
      isLocation: false,
      groupLabel: 'Şehir merkezi (rota)',
      valueText: opt ? opt.label : String(f.max_nav_city_m),
      onRemove: () => applyPatch({ max_nav_city_m: '' }),
    });
  }
  if (f.max_nav_town_m) {
    const opt = NAV_TOWN_MAX_OPTIONS.find((o) => String(o.value) === String(f.max_nav_town_m));
    add({
      id: 'nav-town',
      isLocation: false,
      groupLabel: 'İlçe merkezi (rota)',
      valueText: opt ? opt.label : String(f.max_nav_town_m),
      onRemove: () => applyPatch({ max_nav_town_m: '' }),
    });
  }

  const unitTxt = formatRangeChipPart(f.unit_price_min, f.unit_price_max, '₺/m²');
  if (unitTxt) {
    add({
      id: 'unit-price',
      isLocation: false,
      groupLabel: 'Birim fiyat',
      valueText: unitTxt,
      onRemove: () => applyPatch({ unit_price_min: '', unit_price_max: '' }),
    });
  }

  const totalTxt = formatRangeChipPart(f.total_price_min, f.total_price_max, '₺');
  if (totalTxt) {
    add({
      id: 'total-price',
      isLocation: false,
      groupLabel: 'Toplam fiyat',
      valueText: totalTxt,
      onRemove: () => applyPatch({ total_price_min: '', total_price_max: '' }),
    });
  }

  const gmTxt = formatRangeChipPart(f.gm_min, f.gm_max, '');
  if (gmTxt) {
    add({
      id: 'gm',
      isLocation: false,
      groupLabel: 'Mülk skoru',
      valueText: gmTxt.trim(),
      onRemove: () => applyPatch({ gm_min: '', gm_max: '' }),
    });
  }

  const metaTxt = formatRangeChipPart(f.meta_min, f.meta_max, '');
  if (metaTxt) {
    const metaStarValue = starsFilterValueFromMetaRange(f.meta_min, f.meta_max);
    const metaStarOpt = META_STAR_FILTER_OPTIONS.find((o) => o.value === metaStarValue);
    add({
      id: 'meta',
      isLocation: false,
      groupLabel: 'Genel puan (yıldız)',
      valueText: metaStarOpt ? metaStarOpt.label : metaTxt.trim(),
      onRemove: () => applyPatch({ meta_min: '', meta_max: '' }),
    });
  }

  const puanTxt = formatRangeChipPart(f.puan_min, f.puan_max, '%');
  if (puanTxt) {
    add({
      id: 'puan',
      isLocation: false,
      groupLabel: 'Kullanıcı puanı',
      valueText: puanTxt.trim(),
      onRemove: () => applyPatch({ puan_min: '', puan_max: '' }),
    });
  }

  if (isLandCategoryFilters(f)) {
    if (f.listing_has_road) {
      add({
        id: 'infra-road',
        isLocation: false,
        groupLabel: 'Yol',
        valueText: triInfraLabel(f.listing_has_road),
        onRemove: () => applyPatch({ listing_has_road: '' }),
      });
    }
    if (f.listing_has_water) {
      add({
        id: 'infra-water',
        isLocation: false,
        groupLabel: 'Su',
        valueText: triInfraLabel(f.listing_has_water),
        onRemove: () => applyPatch({ listing_has_water: '' }),
      });
    }
    if (f.listing_has_power_line) {
      add({
        id: 'infra-power',
        isLocation: false,
        groupLabel: 'Elektrik hattı',
        valueText: triInfraLabel(f.listing_has_power_line),
        onRemove: () => applyPatch({ listing_has_power_line: '' }),
      });
    }
  }

  if (f.mine) {
    add({
      id: 'mine',
      isLocation: false,
      groupLabel: 'Kayıt',
      valueText: 'Sadece benim sorgularım',
      onRemove: () => applyPatch({ mine: false }),
    });
  }

  const leafIds = splitCsvParam(f.category_leaf_id);
  leafIds.forEach((leafId) => {
    add({
      id: `leaf-${leafId}`,
      isLocation: false,
      groupLabel: 'Kategori',
      valueText: formatCategoryIdChip(leafId),
      onRemove: () => {
        const next = splitCsvParam(f.category_leaf_id).filter((x) => x !== leafId);
        applyPatch({ category_leaf_id: next.join(',') });
      },
    });
  });

  splitCsvParam(f.category_type).forEach((bid) => {
    add({
      id: `branch-${bid}`,
      isLocation: false,
      groupLabel: 'Alt kategori',
      valueText: formatCategoryIdChip(bid),
      onRemove: () => {
        const next = splitCsvParam(f.category_type).filter((x) => x !== bid);
        applyPatch({ category_type: next.join(',') });
      },
    });
  });

  const hasTree = leafIds.length > 0 || splitCsvParam(f.category_type).length > 0;
  if ((f.category_main || '').trim() && !hasTree) {
    add({
      id: 'cat-main',
      isLocation: false,
      groupLabel: 'Ana kategori',
      valueText: formatCategoryMainChip(f.category_main),
      onRemove: () => applyPatch({ category_main: '' }),
    });
  }

  if (String(f.listing_attr || '').trim()) {
    add({
      id: 'listing-attr',
      isLocation: false,
      groupLabel: 'Kategori detayı',
      valueText: 'Özel alan filtresi',
      onRemove: () => applyPatch({ listing_attr: '' }),
    });
  }

  return chips;
}

/** Bu sayfa satır aralığı (1–10) — toplam 0 ise "0" */
function formatPageRangeLabel(page, pageSize, total) {
  const t = Number(total) || 0;
  const ps = Number(pageSize) || 0;
  const p = Number(page) || 1;
  if (t <= 0 || ps <= 0) return '0';
  const start = (p - 1) * ps + 1;
  const end = Math.min(p * ps, t);
  return `${start}–${end}`;
}

/**
 * Üstte sonuç özeti, altında (varsa) ayraç + filtre chip’leri.
 */
function PortalCompareListLink({ count, returnTo }) {
  return (
    <a href={getPortalComparePageHref(returnTo)} className="pp-portal-compare-link">
      Karşılaştırma Listesi <strong>({count})</strong>
    </a>
  );
}

function PortalListFilterCard({ summaryLine, chips, onClearSecondary, summaryActions = null, listMode = LIST_MODE.QUERIES }) {
  const hasChips = chips && chips.length > 0;
  const hasSecondary = hasChips && chips.some((c) => !c.isLocation);
  return (
    <div
      className={`pp-portal-active-filters pp-portal-active-filters--${listMode}`}
      role="region"
      aria-label="Liste özeti ve seçili filtreler"
    >
      <div className="pp-portal-active-filters__summary-row">
        <div className="pp-portal-active-filters__summary" aria-live="polite">
          {summaryLine}
        </div>
        {summaryActions ? (
          <div className="pp-portal-active-filters__summary-actions">
            {summaryActions}
          </div>
        ) : null}
      </div>
      {hasChips ? (
        <>
          <div className="pp-portal-active-filters__divider" aria-hidden="true" />
          <div className="pp-portal-active-filters__chip-row">
            <div className="pp-portal-active-filters__chips">
              {chips.map((c) => (
                <div key={c.id} className="pp-portal-filter-chip">
                  <span className="pp-portal-filter-chip__group">{c.groupLabel}</span>
                  <span className="pp-portal-filter-chip__box">
                    <span className="pp-portal-filter-chip__value">{c.valueText}</span>
                    <button
                      type="button"
                      className="pp-portal-filter-chip__remove"
                      onClick={c.onRemove}
                      aria-label={`${c.groupLabel}: ${c.valueText} filtresini kaldır`}
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  </span>
                </div>
              ))}
            </div>
            {hasSecondary ? (
              <button type="button" className="pp-portal-active-filters__clear-all" onClick={onClearSecondary}>
                Tümünü Temizle
              </button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

function getPortalTopbarBottomPx() {
  if (typeof document === 'undefined') return 60;
  const topbarEl = document.getElementById('main-topbar');
  if (topbarEl) {
    return Math.max(0, Math.round(topbarEl.getBoundingClientRect().bottom));
  }
  return 60;
}

/** Yüzen mod bildirimi aktifken liste başlığı kopyasını onun altına yerleştirmek için. */
function getPortalModeNoticeFloatingHeightPx() {
  if (typeof document === 'undefined') return 0;
  const floatingNotice = document.querySelector('.pp-portal-mode-notice--floating');
  if (!floatingNotice) return 0;
  return Math.max(0, Math.round(floatingNotice.getBoundingClientRect().height));
}

function getPortalListHeaderStickyTopPx() {
  return getPortalTopbarBottomPx() + getPortalModeNoticeFloatingHeightPx();
}

function StickyModeNotice({ listMode, text }) {
  const hostRef = useRef(null);
  const sentinelRef = useRef(null);
  const [floatingStyle, setFloatingStyle] = useState(null);
  const pinnedRef = useRef(false);

  useEffect(() => {
    if (!hostRef.current || typeof window === 'undefined') return undefined;
    let rafId = null;
    let topbarTimer = null;
    pinnedRef.current = false;
    setFloatingStyle(null);

    const update = () => {
      const hostEl = hostRef.current;
      if (!hostEl) return;
      const hostRect = hostEl.getBoundingClientRect();
      const topOffset = getPortalTopbarBottomPx();

      const enter = hostRect.top <= topOffset + 2;
      const exit = hostRect.top > topOffset + 16;

      let nextStyle = null;
      if (pinnedRef.current) {
        if (exit) {
          pinnedRef.current = false;
          nextStyle = null;
        } else {
          const hostWidth = hostEl.offsetWidth || hostRect.width;
          nextStyle = {
            left: `${Math.round(hostRect.left)}px`,
            width: `${Math.round(hostWidth)}px`,
            top: `${topOffset}px`,
          };
        }
      } else if (enter) {
        pinnedRef.current = true;
        const hostWidth = hostEl.offsetWidth || hostRect.width;
        nextStyle = {
          left: `${Math.round(hostRect.left)}px`,
          width: `${Math.round(hostWidth)}px`,
          top: `${topOffset}px`,
        };
      }

      setFloatingStyle((prev) => {
        if (!nextStyle && !prev) return prev;
        if (!nextStyle) return null;
        if (prev && prev.left === nextStyle.left && prev.width === nextStyle.width && prev.top === nextStyle.top) {
          return prev;
        }
        return nextStyle;
      });
    };

    const scheduleUpdate = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        update();
      });
    };

    const scheduleTopbarResize = () => {
      if (topbarTimer != null) clearTimeout(topbarTimer);
      topbarTimer = window.setTimeout(() => {
        topbarTimer = null;
        scheduleUpdate();
      }, 120);
    };

    update();

    const scrollOpts = { passive: true, capture: true };
    window.addEventListener('scroll', scheduleUpdate, scrollOpts);
    document.addEventListener('scroll', scheduleUpdate, scrollOpts);
    if (typeof window.visualViewport !== 'undefined') {
      window.visualViewport.addEventListener('scroll', scheduleUpdate, scrollOpts);
    }

    window.addEventListener('resize', scheduleUpdate);
    const topbarEl = document.getElementById('main-topbar');
    const topbarRo =
      topbarEl && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleTopbarResize)
        : null;
    topbarRo?.observe(topbarEl);

    const scrollEl = document.scrollingElement || document.documentElement;
    scrollEl.addEventListener('scroll', scheduleUpdate, scrollOpts);

    /* Üst çubuk yüksekliği kadar üstten “kırpılmış” viewport — kaydırınca sınır geçişi yakalanır */
    let ioSentinel = null;
    if (typeof IntersectionObserver !== 'undefined' && sentinelRef.current && topbarEl) {
      const topbarH = Math.max(1, Math.round(topbarEl.getBoundingClientRect().height || 60));
      ioSentinel = new IntersectionObserver(() => scheduleUpdate(), {
        root: null,
        rootMargin: `-${topbarH}px 0px 0px 0px`,
        threshold: [0, 1],
      });
      ioSentinel.observe(sentinelRef.current);
    }

    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      if (topbarTimer != null) clearTimeout(topbarTimer);
      window.removeEventListener('scroll', scheduleUpdate, scrollOpts);
      document.removeEventListener('scroll', scheduleUpdate, scrollOpts);
      scrollEl.removeEventListener('scroll', scheduleUpdate, scrollOpts);
      if (typeof window.visualViewport !== 'undefined') {
        window.visualViewport.removeEventListener('scroll', scheduleUpdate, scrollOpts);
      }
      window.removeEventListener('resize', scheduleUpdate);
      topbarRo?.disconnect();
      ioSentinel?.disconnect();
    };
  }, [listMode]);

  return (
    <>
      <div
        ref={sentinelRef}
        className="pp-portal-mode-notice-scroll-sentinel"
        aria-hidden="true"
      />
      <div className="pp-portal-mode-notice-host" ref={hostRef}>
      <div className={`pp-portal-mode-notice pp-portal-mode-notice--${listMode}`} role="status" aria-live="polite">
        <span className="pp-portal-mode-notice__dot" aria-hidden="true" />
        <span className="pp-portal-mode-notice__text">{text}</span>
      </div>
      {floatingStyle ? (
        <div
          className={`pp-portal-mode-notice pp-portal-mode-notice--${listMode} pp-portal-mode-notice--floating`}
          style={floatingStyle}
          aria-hidden="true"
        >
          <span className="pp-portal-mode-notice__dot" aria-hidden="true" />
          <span className="pp-portal-mode-notice__text">{text}</span>
        </div>
      ) : null}
    </div>
    </>
  );
}

function StickyReplica({ className = '', stickyLabel = '', stickyLabelVariant = 'queries', children }) {
  const hostRef = useRef(null);
  const sentinelRef = useRef(null);
  const [floatingStyle, setFloatingStyle] = useState(null);
  const pinnedRef = useRef(false);

  useEffect(() => {
    if (!hostRef.current || typeof window === 'undefined') return undefined;
    let rafId = null;
    let roTimer = null;
    pinnedRef.current = false;
    setFloatingStyle(null);

    const update = () => {
      const hostEl = hostRef.current;
      if (!hostEl) return;
      const hostRect = hostEl.getBoundingClientRect();
      const topOffset = getPortalListHeaderStickyTopPx();

      const enter = hostRect.top <= topOffset + 2;
      const exit = hostRect.top > topOffset + 16;

      let nextStyle = null;
      if (pinnedRef.current) {
        if (exit) {
          pinnedRef.current = false;
          nextStyle = null;
        } else {
          const hostWidth = hostEl.offsetWidth || hostRect.width;
          nextStyle = {
            left: `${Math.round(hostRect.left)}px`,
            width: `${Math.round(hostWidth)}px`,
            top: `${topOffset}px`,
          };
        }
      } else if (enter) {
        pinnedRef.current = true;
        const hostWidth = hostEl.offsetWidth || hostRect.width;
        nextStyle = {
          left: `${Math.round(hostRect.left)}px`,
          width: `${Math.round(hostWidth)}px`,
          top: `${topOffset}px`,
        };
      }

      setFloatingStyle((prev) => {
        if (!nextStyle && !prev) return prev;
        if (!nextStyle) return null;
        if (prev && prev.left === nextStyle.left && prev.width === nextStyle.width && prev.top === nextStyle.top) {
          return prev;
        }
        return nextStyle;
      });
    };

    const scheduleUpdate = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        update();
      });
    };

    const scheduleResizeObserver = () => {
      if (roTimer != null) clearTimeout(roTimer);
      roTimer = window.setTimeout(() => {
        roTimer = null;
        scheduleUpdate();
      }, 120);
    };

    update();

    const scrollOpts = { passive: true, capture: true };
    window.addEventListener('scroll', scheduleUpdate, scrollOpts);
    document.addEventListener('scroll', scheduleUpdate, scrollOpts);
    if (typeof window.visualViewport !== 'undefined') {
      window.visualViewport.addEventListener('scroll', scheduleUpdate, scrollOpts);
    }

    window.addEventListener('resize', scheduleUpdate);
    const scrollEl = document.scrollingElement || document.documentElement;
    scrollEl.addEventListener('scroll', scheduleUpdate, scrollOpts);

    const ro =
      typeof ResizeObserver !== 'undefined' && hostRef.current
        ? new ResizeObserver(scheduleResizeObserver)
        : null;
    if (ro && hostRef.current) ro.observe(hostRef.current);
    const topbarEl = document.getElementById('main-topbar');
    const topbarRo =
      topbarEl && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleResizeObserver)
        : null;
    topbarRo?.observe(topbarEl);

    const modeNoticeHostEl = document.querySelector('.pp-portal-mode-notice-host');
    const modeNoticeRo =
      modeNoticeHostEl && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleResizeObserver)
        : null;
    modeNoticeRo?.observe(modeNoticeHostEl);

    let ioSentinel = null;
    if (typeof IntersectionObserver !== 'undefined' && sentinelRef.current && topbarEl) {
      const topbarH = Math.max(1, Math.round(topbarEl.getBoundingClientRect().height || 60));
      const noticeH = getPortalModeNoticeFloatingHeightPx();
      const cropTop = Math.max(1, topbarH + noticeH);
      ioSentinel = new IntersectionObserver(() => scheduleUpdate(), {
        root: null,
        rootMargin: `-${cropTop}px 0px 0px 0px`,
        threshold: [0, 1],
      });
      ioSentinel.observe(sentinelRef.current);
    }

    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      if (roTimer != null) clearTimeout(roTimer);
      window.removeEventListener('scroll', scheduleUpdate, scrollOpts);
      document.removeEventListener('scroll', scheduleUpdate, scrollOpts);
      scrollEl.removeEventListener('scroll', scheduleUpdate, scrollOpts);
      if (typeof window.visualViewport !== 'undefined') {
        window.visualViewport.removeEventListener('scroll', scheduleUpdate, scrollOpts);
      }
      window.removeEventListener('resize', scheduleUpdate);
      ro?.disconnect();
      topbarRo?.disconnect();
      modeNoticeRo?.disconnect();
      ioSentinel?.disconnect();
    };
  }, [className]);

  return (
    <div className="pp-portal-sticky-replica-host" ref={hostRef}>
      <div
        ref={sentinelRef}
        className="pp-portal-mode-notice-scroll-sentinel"
        aria-hidden="true"
      />
      <div className={`${className}${stickyLabel ? ' pp-portal-sticky-replica pp-portal-sticky-replica--inline' : ''}`}>
        {stickyLabel ? (
          <span className={`pp-portal-sticky-replica__badge pp-portal-sticky-replica__badge--${stickyLabelVariant}`}>
            {stickyLabel}
          </span>
        ) : null}
        {children}
      </div>
      {floatingStyle ? (
        <div
          className={`${className} pp-portal-sticky-replica pp-portal-sticky-replica--floating`}
          style={floatingStyle}
          aria-hidden="true"
        >
          {stickyLabel ? (
            <span className={`pp-portal-sticky-replica__badge pp-portal-sticky-replica__badge--${stickyLabelVariant}`}>
              {stickyLabel}
            </span>
          ) : null}
          {children}
        </div>
      ) : null}
    </div>
  );
}

const LIST_MODE = PORTAL_LIST_MODE;

function resolveListMode(params) {
  if (!isWebIlanPortalUiEnabled()) return LIST_MODE.QUERIES;
  const raw = (params.get('list_mode') || '').trim().toLowerCase();
  if (raw === LIST_MODE.LISTINGS || raw === LIST_MODE.QUERIES || raw === LIST_MODE.ALL) return raw;
  const ref = (params.get('ref') || '').trim().toLowerCase();
  if (ref === 'vitrin') return LIST_MODE.LISTINGS;
  if (ref === 'recent90' || ref === 'recent30') return LIST_MODE.QUERIES;
  return LIST_MODE.QUERIES;
}

function getInitialFilters() {
  const params = new URLSearchParams(window.location.search);
  return {
    list_mode: resolveListMode(params),
    city_id: params.get('city_id') || '',
    town_id: params.get('town_id') || '',
    quarter_id: params.get('quarter_id') || '',
    query_type: params.get('query_type') || '',
    expert_answered:
      params.get('expert_answered') === '1' || params.get('expert_status') === 'ANSWERED' ? '1' : '',
    price_advantage_only: params.get('price_advantage_only') === '1' ? '1' : '',
    mine: params.get('mine') === '1',
    page: Math.max(1, Number(params.get('page')) || 1),
    page_size: PAGE_SIZE_OPTIONS.includes(Number(params.get('page_size')))
      ? Number(params.get('page_size'))
      : DEFAULT_PAGE_SIZE,
    sort_by: (() => {
      const sb = normalizeSortBy(params.get('sort_by') || '');
      return sb === 'quarter_score' ? '' : sb;
    })(),
    sort_dir: normalizeSortDir(params.get('sort_dir') || 'desc'),
    area_m2_min: params.get('area_m2_min') || '',
    area_m2_max: params.get('area_m2_max') || '',
    unit_price_min: params.get('unit_price_min') || '',
    unit_price_max: params.get('unit_price_max') || '',
    total_price_min: params.get('total_price_min') || '',
    total_price_max: params.get('total_price_max') || '',
    gm_min: params.get('gm_min') || '',
    gm_max: params.get('gm_max') || '',
    quarter_score_min: '',
    quarter_score_max: '',
    meta_min: params.get('meta_min') || '',
    meta_max: params.get('meta_max') || '',
    puan_min: params.get('puan_min') || '',
    puan_max: params.get('puan_max') || '',
    listing_has_road: params.get('listing_has_road') || '',
    listing_has_water: params.get('listing_has_water') || '',
    listing_has_power_line: params.get('listing_has_power_line') || '',
    road_frontage_min_m: params.get('road_frontage_min_m') || '',
    road_frontage_max_m: params.get('road_frontage_max_m') || '',
    max_nav_city_m: params.get('max_nav_city_m') || '',
    max_nav_town_m: params.get('max_nav_town_m') || '',
    category_main: params.get('category_main') || '',
    category_type: params.get('category_type') || '',
    category_leaf_id: params.get('category_leaf_id') || '',
    listing_attr: params.get('listing_attr') || '',
    town_ids: params.get('town_ids') || '',
    quarter_ids: params.get('quarter_ids') || '',
    is_sale: params.get('is_sale') || '',
    listing_type: params.get('listing_type') || '',
    aranacaklar_contact_id: params.get('aranacaklar_contact_id') || '',
    aranacaklar_contact_name: params.get('aranacaklar_contact_name') || '',
    consultant_user_id: params.get('consultant_user_id') || '',
    consultant_user_name: params.get('consultant_user_name') || '',
  };
}

function getRecentQueriesUrl(filters) {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (
      key === 'quarter_score_min' ||
      key === 'quarter_score_max' ||
      (key === 'sort_by' && value === 'quarter_score') ||
      value === '' ||
      value === null ||
      value === undefined ||
      value === false ||
      (key === 'page' && Number(value) === 1) ||
      (key === 'page_size' && Number(value) === DEFAULT_PAGE_SIZE) ||
      (key === 'list_mode' && value === LIST_MODE.QUERIES)
    ) {
      return;
    }
    query.set(key, value === true ? '1' : String(value));
  });
  const search = query.toString();
  return search ? `/portal/recent-queries/?${search}` : '/portal/recent-queries/';
}

function buildQueryState(filters) {
  const nextUrl = getRecentQueriesUrl(filters);
  window.history.replaceState({}, '', nextUrl);
}

function decorateRows(items) {
  return items.map((item) => ({
    ...item,
    _view: {
      queryTypeLabel: queryTypeLabel(item.query_type),
      parcelShapeLabel: normalizeParcelShapeLabel(item.parcel_shape_type_label || item.parcel_shape_type) || '',
      areaText: formatArea(item.area_m2),
      unitPriceText: `${formatCurrency(item.unit_price)}/m²`,
      totalPriceText: formatCurrency(item.total_price),
      dateText: formatDate(item.created_at),
      titleText: item.title || item.quarter_name || '—',
      adaParselText: `${item.ada || '0'}/${item.parsel || '0'}`,
      roadFrontageText: formatRoadFrontageMeters(item.road_frontage_m),
      navCityKmText: formatNavRouteKm(item.nav_in_distance_m_city),
      navTownKmText: formatNavRouteKm(item.nav_in_distance_m_town),
    },
  }));
}

const ResultThumb = memo(function ResultThumb({ src, title, priority = false }) {
  const [imageFailed, setImageFailed] = useState(false);

  if (!src || imageFailed) {
    return (
      <div className="pp-portal-card-thumb-placeholder" aria-hidden="true">
        <i className="fas fa-map" />
      </div>
    );
  }

  return (
    <div className="pp-portal-card-thumb-wrap">
      <img
        className="pp-portal-card-thumb"
        src={src}
        alt={title || ''}
        width={320}
        height={180}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={priority ? 'high' : 'auto'}
        onError={() => setImageFailed(true)}
      />
    </div>
  );
});

const ResultCard = memo(function ResultCard({
  item,
  style,
  returnTo,
  compareChecked = false,
  compareDisabled = false,
  compareTitle = '',
  onCompareToggle,
  thumbPriority = false,
}) {
  const expertText = item.has_expert_request
    ? item.expert_status === 'ANSWERED'
      ? 'Uzman Yanıtı'
      : 'Bekleniyor'
    : '—';
  const commentCount = Math.max(0, Number(item.comment_count ?? 0) || 0);
  const favoriteCount = Math.max(0, Number(item.favorite_count_total ?? 0) || 0);
  const ratingSuccessCount = resolveRatingSuccessCount(item);
  const starBlendItem = buildPortalStarBlendItemFromListRow(item);
  const metaCombinedPct = resolveListRowMetaStarsPct(item);
  const araziText = formatListingInvestmentScore(metaCombinedPct ?? item.investment_normalized_score);
  const detailUrl = `/portal/recent-queries/${item.snapshot_id}/?returnTo=${encodeURIComponent(returnTo)}`;
  const compareId = `pp-portal-compare-query-${item.snapshot_id}`;
  const prefetchDetail = useCallback(() => {
    prefetchPortalDetailShell(item.snapshot_id);
    prefetchPortalDetailPageChunk();
  }, [item.snapshot_id]);

  return (
    <div className="pp-portal-card-compare-wrap">
      <a
        href={detailUrl}
        className="pp-portal-card pp-portal-card--listing"
        style={style}
        onMouseEnter={prefetchDetail}
        onFocus={prefetchDetail}
      >
        <div className="pp-portal-card-grid">
          <ResultThumb src={item.thumbnail_url} title={item._view?.titleText} priority={thumbPriority} />
          <div className="pp-portal-card-main">
            <div className="pp-portal-card-meta">
              <span className="pp-chip">{item._view?.queryTypeLabel}</span>
              {item.has_expert_request ? <span className="pp-chip pp-chip-warn">Uzman</span> : null}
              {item.comment_count ? <span className="pp-chip pp-chip-social">Yorum {item.comment_count}</span> : null}
            </div>
            <strong>{item._view?.titleText || '—'}</strong>
            <span>
              Ada/Parsel: {item._view?.adaParselText || '0/0'}
              {item._view?.parcelShapeLabel ? ` / ${item._view.parcelShapeLabel}` : ''}
            </span>
          </div>
          <div className="pp-portal-card-metric pp-portal-card-metric-area">
            <strong>{item._view?.areaText}</strong>
          </div>
          <div className="pp-portal-card-metric pp-portal-card-metric-road">
            <strong>{item._view?.roadFrontageText}</strong>
          </div>
          <div className="pp-portal-card-metric pp-portal-card-metric-total">
            <strong>{item._view?.totalPriceText}</strong>
            <span className="pp-portal-card-unit-sub">{item._view?.unitPriceText}</span>
          </div>
          <div className="pp-portal-card-side pp-portal-card-side-rating pp-portal-card-side--scores">
            <div className="pp-portal-card-scores-stars">
              <PortalMetaStars
                scorePct={metaCombinedPct}
                showOutOfFive
                listingCategoryMain={starBlendItem.listing_category_main ?? starBlendItem.category_main}
                categoryLeafId={starBlendItem.listing_category_leaf_id ?? starBlendItem.category_leaf_id}
                queryType={starBlendItem.query_type}
                imarsizLandStarBlend={starBlendItem.imarsiz_land_star_blend}
              />
            </div>
            <div className="pp-portal-listing-social-row" title="Favori / başarılı / yorum">
              <span className="pp-portal-listing-social-badge pp-portal-listing-social-badge--fav" title="Favori sayısı">
                <i className="fas fa-heart" aria-hidden="true" /> {favoriteCount}
              </span>
              <span className="pp-portal-listing-social-badge pp-portal-listing-social-badge--ok" title="Başarılı puan adedi">
                <i className="fas fa-thumbs-up" aria-hidden="true" /> {ratingSuccessCount}
              </span>
              <span className="pp-portal-listing-social-badge pp-portal-listing-social-badge--comment" title="Yorum sayısı">
                <i className="fas fa-comment-dots" aria-hidden="true" /> {commentCount}
              </span>
            </div>
            <span className="pp-portal-listing-score-line" title="Mülk skoru">
              {PORTAL_LIST_INVESTMENT_SCORE_LABEL} {araziText}
            </span>
          </div>
          <div className="pp-portal-card-side pp-portal-card-side-expert">
            <time>{item._view?.dateText}</time>
            <span className="pp-portal-nav-km-line" title="İl merkezine rota">
              İl {item._view?.navCityKmText}
            </span>
            <span className="pp-portal-nav-km-line" title="İlçe merkezine rota">
              İlçe {item._view?.navTownKmText}
            </span>
            {item.has_expert_request ? (
              <span
                className={[
                  'pp-expert-badge',
                  item.expert_status === 'ANSWERED' ? 'answered' : 'pending',
                ].join(' ')}
              >
                {expertText}
              </span>
            ) : null}
          </div>
          <div className="pp-portal-card-side pp-portal-card-side-compare">
            <label
              className={`pp-portal-card-compare-control pp-portal-card-compare-control--column${compareDisabled ? ' is-disabled' : ''}`}
              htmlFor={compareId}
              title={compareTitle || undefined}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <span>Karşılaştır</span>
              <input
                id={compareId}
                type="checkbox"
                checked={compareChecked}
                disabled={compareDisabled}
                onChange={(event) => {
                  event.stopPropagation();
                  onCompareToggle?.();
                }}
              />
            </label>
          </div>
        </div>
      </a>
    </div>
  );
});

function SortIndicator({ isActive, direction }) {
  if (!isActive) return null;
  return <span className="pp-sort-indicator" aria-hidden="true">{direction === 'desc' ? 'v' : '^'}</span>;
}

/** Tek satır iskelet kart (tıklanamaz). */
function PortalPlaceholderCardRow({ kind }) {
  const icon = kind === 'listing' ? 'fa-home' : 'fa-map';
  const lastSideClass =
    kind === 'listing'
      ? 'pp-portal-card-side pp-portal-card-side-rating pp-portal-card-side-rating--listing'
      : 'pp-portal-card-side pp-portal-card-side-expert';
  return (
    <div
      className="pp-portal-card pp-portal-card--listing pp-portal-card--placeholder"
      aria-hidden="true"
    >
      <div className="pp-portal-card-grid">
        <div className="pp-portal-card-thumb-placeholder pp-portal-placeholder-thumb">
          <i className={`fas ${icon}`} />
        </div>
        <div className="pp-portal-card-main">
          <div className="pp-portal-placeholder-chip" />
          <div className="pp-portal-placeholder-line pp-portal-placeholder-line--title" />
          <div className="pp-portal-placeholder-line pp-portal-placeholder-line--sub" />
        </div>
        <div className="pp-portal-card-metric pp-portal-card-metric-area">
          <div className="pp-portal-placeholder-line pp-portal-placeholder-line--metric" />
        </div>
        <div className="pp-portal-card-metric pp-portal-card-metric-road">
          <div className="pp-portal-placeholder-line pp-portal-placeholder-line--metric" />
        </div>
        <div className="pp-portal-card-metric pp-portal-card-metric-total">
          <div className="pp-portal-placeholder-line pp-portal-placeholder-line--price" />
          <div className="pp-portal-placeholder-line pp-portal-placeholder-line--unit" />
        </div>
        <div className="pp-portal-card-side pp-portal-card-side-rating pp-portal-card-side--scores">
          <div className="pp-portal-placeholder-stars" />
          <div className="pp-portal-placeholder-line pp-portal-placeholder-line--tiny" />
          <div className="pp-portal-placeholder-line pp-portal-placeholder-line--tiny" />
        </div>
        <div className={lastSideClass}>
          <div className="pp-portal-placeholder-line pp-portal-placeholder-line--date" />
          {kind === 'listing' ? (
            <div className="pp-portal-placeholder-line pp-portal-placeholder-line--tiny" />
          ) : (
            <div className="pp-portal-placeholder-line pp-portal-placeholder-line--expert" />
          )}
        </div>
        <div className="pp-portal-card-side pp-portal-card-side-compare">
          <div className="pp-portal-placeholder-line pp-portal-placeholder-line--tiny" />
          <div className="pp-portal-placeholder-line pp-portal-placeholder-line--tiny" />
        </div>
      </div>
    </div>
  );
}

/**
 * Sonuç yokken veya il seçilmeden önce: gerçek kart düzenine benzeyen çoklu placeholder.
 * @param {string|null} [message] — null ise alt yazı gösterilmez (üstte ayrı uyarı varsa).
 */
function PortalListEmptyPlaceholder({ kind, message, count = 3 }) {
  const n = Math.max(1, Math.min(8, count));
  return (
    <div className="pp-portal-list-empty-block" role="status" aria-live="polite">
      <div className="pp-portal-placeholder-cards">
        {Array.from({ length: n }, (_, i) => (
          <PortalPlaceholderCardRow key={i} kind={kind} />
        ))}
      </div>
      {message ? <p className="pp-portal-placeholder-caption">{message}</p> : null}
    </div>
  );
}

/**
 * Tablo alanı yüklenirken (sayfa/filtre geçişi) içerik kısalınca sayfa yüksekliği
 * düşmesin diye son ölçülen yüksekliği min-height olarak tutar.
 */
function PortalListBody({ lockMinHeight, children }) {
  const innerRef = useRef(null);
  const [minHeight, setMinHeight] = useState(0);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return undefined;

    const measure = () => {
      const next = Math.ceil(el.getBoundingClientRect().height);
      if (next <= 0) return;
      setMinHeight((prev) => (lockMinHeight ? prev : next));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [lockMinHeight]);

  useLayoutEffect(() => {
    if (lockMinHeight) return;
    const el = innerRef.current;
    if (!el) return;
    const next = Math.ceil(el.getBoundingClientRect().height);
    if (next > 0) setMinHeight(next);
  }, [lockMinHeight, children]);

  const style = minHeight > 0 ? { minHeight: `${minHeight}px` } : undefined;

  return (
    <div className="pp-portal-list-body" style={style}>
      <div ref={innerRef} className="pp-portal-list-body__content">
        {children}
      </div>
    </div>
  );
}

function SortHeaderButton({ label, field, activeField, direction, onSortChange }) {
  const isActive = activeField === field;
  return (
    <button
      type="button"
      className={`pp-portal-sort-btn${isActive ? ' is-active' : ''}`}
      onClick={() => onSortChange(field)}
      aria-pressed={isActive}
    >
      <span>{label}</span>
      <SortIndicator isActive={isActive} direction={direction} />
    </button>
  );
}

function HeaderSortDropdown({ sortBy, sortDir, onSortChange }) {
  const options = [
    { label: 'Kullanıcı Puanı', field: 'rating' },
    { label: 'Yorum', field: 'comment' },
    { label: PORTAL_LIST_INVESTMENT_SCORE_LABEL, field: 'gm' },
    { label: 'Genel Puan (yıldız)', field: 'meta' },
  ];
  const activeOption = options.find((option) => option.field === sortBy) || null;

  return (
    <details className="pp-portal-header-sort-dropdown">
      <summary className="pp-portal-header-sort-dropdown__trigger">
        <span className="pp-portal-header-sort-dropdown__title">Sıralama</span>
        <span className="pp-portal-header-sort-dropdown__value">
          {activeOption ? activeOption.label : 'Seçiniz'}
        </span>
        <SortIndicator isActive={Boolean(activeOption)} direction={sortDir} />
      </summary>
      <div className="pp-portal-header-sort-dropdown__menu">
        {options.map((option) => {
          const isActive = sortBy === option.field;
          return (
            <button
              key={option.field}
              type="button"
              className={`pp-portal-header-sort-dropdown__item${isActive ? ' is-active' : ''}`}
              onClick={(event) => {
                onSortChange(option.field);
                event.currentTarget.closest('details')?.removeAttribute('open');
              }}
            >
              <span>{option.label}</span>
              <SortIndicator isActive={isActive} direction={sortDir} />
            </button>
          );
        })}
      </div>
    </details>
  );
}

function ResultsTableHeader({ sortBy, sortDir, onSortChange }) {
  return (
    <StickyReplica
      className="pp-portal-list-header"
      stickyLabel="ProSorgular"
      stickyLabelVariant="queries"
    >
      <div className="pp-portal-list-header-col" />
      <div className="pp-portal-list-header-col">İlan</div>
      <div className="pp-portal-list-header-col pp-portal-list-header-col-right">Alan</div>
      <div className="pp-portal-list-header-col pp-portal-list-header-col-right pp-portal-list-header-col--road-frontage">
        {PORTAL_LIST_ROAD_HEADER}
      </div>
      <div className="pp-portal-list-header-col pp-portal-list-header-col-right">Toplam</div>
      <div className="pp-portal-list-header-col pp-portal-list-header-col-right pp-portal-list-header-col-stack pp-portal-list-header-col--query-rating-stack">
        <HeaderSortDropdown sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} />
      </div>
      <div className="pp-portal-list-header-col pp-portal-list-header-col-right pp-portal-list-header-col-stack pp-portal-list-header-col--expert-nav">
        <SortHeaderButton label="Uzman Yanıtı" field="expert" activeField={sortBy} direction={sortDir} onSortChange={onSortChange} />
        <span className="pp-portal-list-header-nav-sub">İl km</span>
        <span className="pp-portal-list-header-nav-sub">İlçe km</span>
      </div>
      <div className="pp-portal-list-header-col pp-portal-list-header-col-right pp-portal-list-header-col-stack pp-portal-list-header-col--compare">
        <span>Karşılaştır</span>
      </div>
    </StickyReplica>
  );
}

function PortalListNoResults({ message }) {
  return (
    <div className="pp-portal-no-results" role="status">
      <p>{message}</p>
    </div>
  );
}

function PortalGuestProsorguInviteCard({
  badge = 'ProSorgu Erişimi',
  title = 'ProSorgu sonuçları uzman üyeler için hazır.',
  text = 'ProSorgu sonuçlarını yalnızca Emlak, SPK, Harita ve LİHKAB firmaları uzmanları görebilir. Bu gruplardan birindeyseniz üyelik oluşturup giriş yaparak sonuç ekranını açabilirsiniz.',
  primaryHref = '/accounts/register/?next=/portal/recent-queries/?ref=recent90',
  primaryLabel = 'Üyelik Oluştur',
  secondaryHref = '/accounts/login/?next=/portal/recent-queries/?ref=recent90',
  secondaryLabel = 'Giriş Yap',
}) {
  return (
    <div className="pp-portal-guest-invite" role="status" aria-live="polite">
      <div className="pp-portal-guest-invite__badge">{badge}</div>
      <h3 className="pp-portal-guest-invite__title">{title}</h3>
      <p className="pp-portal-guest-invite__text">{text}</p>
      <div className="pp-portal-guest-invite__actions">
        <a className="pp-portal-guest-invite__primary" href={primaryHref}>
          {primaryLabel}
        </a>
        {secondaryHref ? (
          <a className="pp-portal-guest-invite__secondary" href={secondaryHref}>
            {secondaryLabel}
          </a>
        ) : null}
      </div>
    </div>
  );
}

function ResultsList({
  items,
  onSortChange,
  returnTo,
  sortBy,
  sortDir,
  filterBar,
  compareState,
}) {
  if (!items.length) {
    return (
      <>
        {filterBar}
        <ResultsTableHeader sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} />
        <div className="pp-portal-results-list">
          <PortalListNoResults message="Bu filtreyle sonuç bulunamadı." />
        </div>
      </>
    );
  }

  return (
    <>
      {filterBar}
      <ResultsTableHeader sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} />
      <PortalRecentQueriesVirtualList
        kind="query"
        items={items}
        renderRow={({ item, index }) => (
          <ResultCard
            key={item.snapshot_id}
            item={item}
            returnTo={returnTo}
            style={{ position: 'static', transform: 'none' }}
            thumbPriority={index < 4}
            compareChecked={compareState.isSelected(item)}
            compareDisabled={compareState.isDisabled(item)}
            compareTitle={compareState.getDisabledReason(item)}
            onCompareToggle={() => compareState.toggleQuery(item)}
          />
        )}
      />
    </>
  );
}

function ListingResultsHeader({ sortBy, sortDir, onSortChange }) {
  return (
    <StickyReplica
      className="pp-portal-list-header pp-portal-list-header--listings"
      stickyLabel="İlanlar"
      stickyLabelVariant="listings"
    >
      <div className="pp-portal-list-header-col" />
      <div className="pp-portal-list-header-col">İlan</div>
      <div className="pp-portal-list-header-col pp-portal-list-header-col-right">Alan</div>
      <div className="pp-portal-list-header-col pp-portal-list-header-col-right pp-portal-list-header-col--road-frontage">
        {PORTAL_LIST_ROAD_HEADER}
      </div>
      <div className="pp-portal-list-header-col pp-portal-list-header-col-right">Toplam</div>
      <div className="pp-portal-list-header-col pp-portal-list-header-col-right pp-portal-list-header-col-stack pp-portal-list-header-col--query-rating-stack">
        <HeaderSortDropdown sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} />
      </div>
      <div className="pp-portal-list-header-col pp-portal-list-header-col-right pp-portal-list-header-col-stack pp-portal-list-header-col--listing-meta">
        <span>Tarih / Konum</span>
        <span className="pp-portal-list-header-nav-sub">İl km</span>
        <span className="pp-portal-list-header-nav-sub">İlçe km</span>
      </div>
      <div className="pp-portal-list-header-col pp-portal-list-header-col-right pp-portal-list-header-col-stack pp-portal-list-header-col--compare">
        <span>Konum</span>
        <span className="pp-portal-list-header-nav-sub">Karşılaştır</span>
      </div>
    </StickyReplica>
  );
}

function ListingResultsList({
  items,
  returnTo,
  sortBy,
  sortDir,
  onSortChange,
  filterBar,
  compareState,
}) {
  if (!items.length) {
    return (
      <>
        {filterBar}
        <ListingResultsHeader sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} />
        <div className="pp-portal-results-list">
          <PortalListNoResults message="Bu filtreyle vitrin ilanı bulunamadı." />
        </div>
      </>
    );
  }
  return (
    <>
      {filterBar}
      <ListingResultsHeader sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} />
      <PortalRecentQueriesVirtualList
        kind="listing"
        items={items}
        renderRow={({ item, index }) => (
          <ListingResultCard
            key={String(item.listing_id)}
            item={item}
            style={{ position: 'static', transform: 'none' }}
            returnTo={returnTo}
            thumbPriority={index < 4}
            compareChecked={compareState.isSelected(item)}
            compareDisabled={compareState.isDisabled(item)}
            compareTitle={compareState.getDisabledReason(item)}
            onCompareToggle={() => compareState.toggleListing(item)}
          />
        )}
      />
    </>
  );
}

export function PortalRecentQueriesApp() {
  const isAuthenticated = usePpPortalAuthenticated();
  const memberType = typeof window !== 'undefined' ? String(window.PP_USER_MEMBER_TYPE || '').trim().toLowerCase() : '';
  const isAdminUser = typeof window !== 'undefined' && Boolean(window.PP_USER_IS_ADMIN);
  const serverCanAccessProsorgu = readPpCanAccessProsorgu();
  const canAccessProsorgu = isAuthenticated && serverCanAccessProsorgu;
  const isIndividualMember = isAuthenticated && !canAccessProsorgu && !isAdminUser;
  const canAccessVault = isAuthenticated && readPpCanAccessVault();
  const prosorguInviteProps = isIndividualMember
    ? {
        badge: 'Bireysel Üyelik',
        title: 'ProSorgu sonuçları bireysel üyelikte kapalıdır.',
        text: 'Bu ekran yalnızca uzman, danışman ve kurumsal üyelik tipleri için açıktır. Bireysel hesabınızı uygun bir profesyonel üyelik tipine yükselttikten sonra ProSorgu sonuç sayfasını görüntüleyebilirsiniz.',
        primaryHref: '/accounts/profile/',
        primaryLabel: 'Profilimi Aç',
        secondaryHref: '/',
        secondaryLabel: 'Ana Sayfaya Dön',
      }
    : {
        badge: 'ProSorgu Erişimi',
        title: 'ProSorgu sonuçları uzman üyeler için hazır.',
        text: 'ProSorgu sonuçlarını yalnızca Emlak, SPK, Harita ve LİHKAB firmaları uzmanları görebilir. Bu gruplardan birindeyseniz üyelik oluşturup giriş yaparak sonuç ekranını açabilirsiniz.',
        primaryHref: '/accounts/register/?next=/portal/recent-queries/?ref=recent90',
        primaryLabel: 'Üyelik Oluştur',
        secondaryHref: '/accounts/login/?next=/portal/recent-queries/?ref=recent90',
        secondaryLabel: 'Giriş Yap',
      };
  const vaultInviteProps = isAuthenticated
    ? {
        badge: 'Sandık (Gizli İlan)',
        title: 'Sandık yalnızca danışman ve kurumsal üyelere açıktır.',
        text: 'Gizli ilanlar danışman, kurumsal (broker) ve admin üyelerimiz tarafından görüntülenebilir. Uygun profesyonel üyelik için profilinizi kontrol edin veya destek ile iletişime geçin.',
        primaryHref: '/accounts/profile/',
        primaryLabel: 'Profilimi Aç',
        ...(isWebIlanPortalUiEnabled()
          ? {
              secondaryHref: '/portal/ilan/olustur/',
              secondaryLabel: 'İlan Ver',
            }
          : {}),
      }
    : {
        badge: 'Sandık (Gizli İlan)',
        title: 'Sandık yalnızca danışman ve kurumsal üyelere açıktır.',
        text: 'Gizli ilan portalı halka açık değildir. Danışman veya kurumsal üyelik ile giriş yaparak Sandık ilanlarını görüntüleyebilirsiniz.',
        primaryHref: '/accounts/register/?next=/portal/recent-queries/?list_mode=all',
        primaryLabel: 'Üyelik Oluştur',
        secondaryHref: '/accounts/login/?next=/portal/recent-queries/?list_mode=all',
        secondaryLabel: 'Giriş Yap',
      };

  const [draftFilters, setDraftFilters] = useState(getInitialFilters);
  const [appliedFilters, setAppliedFilters] = useState(getInitialFilters);
  const [activeRatingSnapshot, setActiveRatingSnapshot] = useState(null);
  const [defaultCityNotice, setDefaultCityNotice] = useState(null);
  const [defaultCityResolved, setDefaultCityResolved] = useState(Boolean(getUrlCityId()));
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);
  const panelRef = useRef(null);
  const [updateButtonStyle, setUpdateButtonStyle] = useState(null);
  const initialUrlCityIdRef = useRef(getUrlCityId());
  const didAutoSelectDefaultCityRef = useRef(Boolean(initialUrlCityIdRef.current));
  const locationCountsFor =
    appliedFilters.list_mode === LIST_MODE.LISTINGS || appliedFilters.list_mode === LIST_MODE.ALL
      ? 'listings'
      : 'queries';

  const citiesQuery = useQuery({
    queryKey: ['portal-locations', 'cities', locationCountsFor],
    queryFn: () => getPortalLocations({ countsFor: locationCountsFor }),
  });

  const cityRows = citiesQuery.data?.cities || [];

  const townsQuery = useQuery({
    queryKey: ['portal-locations', 'towns', draftFilters.city_id, locationCountsFor],
    queryFn: () => getPortalLocations({
      cityId: draftFilters.city_id,
      countsFor: locationCountsFor,
    }),
    enabled: Boolean(draftFilters.city_id),
  });

  const quartersQuery = useQuery({
    queryKey: ['portal-locations', 'quarters', draftFilters.city_id, draftFilters.town_id, locationCountsFor],
    queryFn: () => getPortalLocations({
      cityId: draftFilters.city_id,
      townId: draftFilters.town_id,
      countsFor: locationCountsFor,
    }),
    enabled: Boolean(draftFilters.city_id && draftFilters.town_id),
  });

  /** Uygulanan il/ilçe/mahalle adları — chip etiketi (taslak ile farklı olabilir) */
  const appliedTownsForChipsQuery = useQuery({
    queryKey: ['portal-locations', 'towns', appliedFilters.city_id, locationCountsFor],
    queryFn: () => getPortalLocations({
      cityId: appliedFilters.city_id,
      countsFor: locationCountsFor,
    }),
    enabled: Boolean(appliedFilters.city_id),
  });

  const appliedQuartersForChipsQuery = useQuery({
    queryKey: ['portal-locations', 'quarters', appliedFilters.city_id, appliedFilters.town_id, locationCountsFor],
    queryFn: () => getPortalLocations({
      cityId: appliedFilters.city_id,
      townId: appliedFilters.town_id,
      countsFor: locationCountsFor,
    }),
    enabled: Boolean(appliedFilters.city_id && appliedFilters.town_id),
  });

  useEffect(() => {
    if (!draftFilters.city_id) {
      setDraftFilters((current) => ({ ...current, town_id: '', quarter_id: '' }));
    }
  }, [draftFilters.city_id]);

  useEffect(() => {
    if (!draftFilters.town_id) {
      setDraftFilters((current) => ({ ...current, quarter_id: '' }));
    }
  }, [draftFilters.town_id]);

  useEffect(() => {
    const appliedCityId = String(appliedFilters.city_id || '').trim();
    if (appliedCityId) {
      setStoredPortalRecentCityId(appliedCityId);
    }
  }, [appliedFilters.city_id]);

  useEffect(() => {
    if (didAutoSelectDefaultCityRef.current) return;
    if (initialUrlCityIdRef.current) {
      didAutoSelectDefaultCityRef.current = true;
      setDefaultCityResolved(true);
      return;
    }
    if (draftFilters.city_id || appliedFilters.city_id) {
      didAutoSelectDefaultCityRef.current = true;
      setDefaultCityResolved(true);
      return;
    }
    if (draftFilters.consultant_user_id || appliedFilters.consultant_user_id) {
      didAutoSelectDefaultCityRef.current = true;
      setDefaultCityResolved(true);
      return;
    }
    if (!citiesQuery.isSuccess) return;

    didAutoSelectDefaultCityRef.current = true;
    const { city, source } = pickDefaultCity(cityRows, getStoredPortalRecentCityId());
    if (!city) {
      setDefaultCityResolved(true);
      return;
    }

    const nextCityId = String(city.id);
    setDraftFilters((current) => ({
      ...current,
      city_id: nextCityId,
      town_id: '',
      quarter_id: '',
      page: 1,
    }));
    setAppliedFilters((current) => ({
      ...current,
      city_id: nextCityId,
      town_id: '',
      quarter_id: '',
      page: 1,
    }));
    setDefaultCityNotice({
      cityId: nextCityId,
      cityName: String(city.name || ''),
      source,
    });
    setDefaultCityResolved(true);
  }, [
    appliedFilters.city_id,
    appliedFilters.consultant_user_id,
    citiesQuery.isSuccess,
    cityRows,
    draftFilters.city_id,
    draftFilters.consultant_user_id,
  ]);

  useEffect(() => {
    if (!defaultCityNotice) return;
    if (String(appliedFilters.city_id || '').trim() !== String(defaultCityNotice.cityId || '').trim()) {
      setDefaultCityNotice(null);
    }
  }, [appliedFilters.city_id, defaultCityNotice]);

  const listMode = appliedFilters.list_mode;
  const hasQueryListScope = Boolean(
    appliedFilters.city_id || appliedFilters.consultant_user_id || appliedFilters.mine,
  );
  const hasVisibleListScope = listMode === LIST_MODE.QUERIES
    ? hasQueryListScope
    : Boolean(appliedFilters.city_id);
  const listingFilterKey = useMemo(
    () => listingSearchCacheKey(appliedFilters),
    [appliedFilters],
  );
  const listingCursorsRef = useRef({});

  useEffect(() => {
    listingCursorsRef.current = {};
  }, [listingFilterKey]);

  const serializedQueryFilters = useMemo(
    () => serializePortalFiltersForQuery(appliedFilters),
    [appliedFilters],
  );

  const listQuery = useQuery({
    queryKey: ['portal-results', serializedQueryFilters],
    enabled: hasQueryListScope
      && canAccessProsorgu
      && listMode === LIST_MODE.QUERIES,
    placeholderData: keepPreviousData,
    initialData: () => readPortalRecentQueryCache(serializedQueryFilters),
    // Cache'den başlangıç verisi göstersek bile mount'ta mutlaka yeniden çek.
    initialDataUpdatedAt: 0,
    staleTime: 15 * 60_000,
    gcTime: 30 * 60_000,
    refetchInterval: (query) => (
      listHasPendingThumbnails(query.state.data) ? 20_000 : false
    ),
    queryFn: () => {
      const { list_mode: _lm, expert_answered: _ea, ...rest } = appliedFilters;
      const {
        aranacaklar_contact_id: _ac,
        aranacaklar_contact_name: _an,
        consultant_user_name: _cun,
        quarter_score_min: _qmin,
        quarter_score_max: _qmax,
        ...restq
      } = rest;
      const q = {
        ...restq,
        page: appliedFilters.page,
        page_size: appliedFilters.page_size,
        mine: appliedFilters.mine ? '1' : '',
      };
      if (appliedFilters.expert_answered === '1') {
        q.expert_status = 'ANSWERED';
      }
      if (q.sort_by === 'quarter_score') {
        delete q.sort_by;
      }
      return getPortalRecentQueries(q);
    },
  });

  useEffect(() => {
    if (listMode !== LIST_MODE.QUERIES) return;
    if (!hasQueryListScope) return;
    if (!listQuery.data || listQuery.isError) return;
    writePortalRecentQueryCache(serializedQueryFilters, listQuery.data);
  }, [
    listMode,
    hasQueryListScope,
    serializedQueryFilters,
    listQuery.data,
    listQuery.isError,
  ]);

  const listingsQuery = useQuery({
    queryKey: ['portal-listings', listingFilterKey, appliedFilters.page],
    enabled: Boolean(appliedFilters.city_id)
      && (
        listMode === LIST_MODE.LISTINGS
        || (listMode === LIST_MODE.ALL && canAccessVault)
      ),
    placeholderData: keepPreviousData,
    staleTime: 120_000,
    queryFn: async () => {
      const page = Math.max(1, Number(appliedFilters.page) || 1);
      const cursor = listingCursorsRef.current[page];
      const params = buildListingSearchParams(appliedFilters, cursor);
      const res = await searchPublishedListings(params);
      const next = res?.pagination?.next_cursor;
      if (next?.updated_at && next?.listing_id) {
        listingCursorsRef.current[page + 1] = next;
      } else {
        delete listingCursorsRef.current[page + 1];
      }
      return res;
    },
  });

  const consultantUsersQuery = useQuery({
    queryKey: ['portal-consultant-users'],
    queryFn: getPortalConsultantUsers,
    enabled: Boolean(isAuthenticated),
    staleTime: 5 * 60_000,
  });

  const rawItems = useMemo(() => listQuery.data?.results || [], [listQuery.data]);
  const rawListingItems = useMemo(
    () => listingsQuery.data?.items || [],
    [listingsQuery.data],
  );

  const items = useMemo(() => decorateRows(rawItems), [rawItems]);
  const totalCount = listQuery.data?.total || 0;
  const listingTotal = listingsQuery.data?.pagination?.total_count ?? 0;
  const pageSize = appliedFilters.page_size || DEFAULT_PAGE_SIZE;
  const totalPagesQueries = Math.max(1, Math.ceil(totalCount / pageSize));
  const totalPagesListings = Math.max(1, Math.ceil(listingTotal / pageSize));
  const totalPages = listMode === LIST_MODE.QUERIES
    ? totalPagesQueries
    : totalPagesListings;
  const navTotalPages = totalPages;
  const nextPageDisabled = appliedFilters.page >= totalPages;
  const listingItems = useMemo(() => rawListingItems, [rawListingItems]);
  const consultantUserOptions = useMemo(
    () => Array.isArray(consultantUsersQuery.data?.users) ? consultantUsersQuery.data.users : [],
    [consultantUsersQuery.data],
  );
  const currentListUrl = useMemo(() => getRecentQueriesUrl(appliedFilters), [appliedFilters]);
  const compareSelection = usePortalCompareSelection();

  const compareState = useMemo(() => {
    const firstGroup = compareSelection.items[0]?.compareGroup || '';
    const isSelected = (itemLike) => {
      const candidate = itemLike?.listing_id || itemLike?.portal_snapshot_id
        ? buildPortalCompareItemFromListingRow(itemLike)
        : buildPortalCompareItemFromQueryRow(itemLike);
      return candidate ? compareSelection.selectedKeySet.has(candidate.key) : false;
    };
    const getDisabledReason = (itemLike) => {
      const candidate = itemLike?.listing_id || itemLike?.portal_snapshot_id
        ? buildPortalCompareItemFromListingRow(itemLike)
        : buildPortalCompareItemFromQueryRow(itemLike);
      if (!candidate) return 'Bu kayıt karşılaştırma için uygun değil.';
      const selected = compareSelection.selectedKeySet.has(candidate.key);
      if (selected) return '';
      if (compareSelection.count >= PORTAL_COMPARE_MAX_ITEMS) {
        return `En fazla ${PORTAL_COMPARE_MAX_ITEMS} kayıt seçebilirsiniz.`;
      }
      if (firstGroup && candidate.compareGroup !== firstGroup) {
        return 'Bu kayıt mevcut karşılaştırma grubuyla uyumlu değil.';
      }
      return '';
    };
    const isDisabled = (itemLike) => Boolean(getDisabledReason(itemLike));
    const toggleResult = (result) => {
      if (!result.ok && result.message) window.alert(result.message);
    };
    return {
      count: compareSelection.count,
      isSelected,
      isDisabled,
      getDisabledReason,
      toggleQuery(item) {
        const candidate = buildPortalCompareItemFromQueryRow(item, {
          detailHref: `/portal/recent-queries/${item.snapshot_id}/?returnTo=${encodeURIComponent(currentListUrl)}`,
        });
        toggleResult(candidate ? togglePortalCompareItem(candidate) : { ok: false, message: 'Kayıt seçilemedi.' });
      },
      toggleListing(item) {
        const candidate = buildPortalCompareItemFromListingRow(item, {
          detailHref: item?.portal_snapshot_id
            ? `/portal/recent-queries/${encodeURIComponent(String(item.portal_snapshot_id))}/?returnTo=${encodeURIComponent(currentListUrl)}`
            : listingDetailHref(item, currentListUrl),
        });
        toggleResult(candidate ? togglePortalCompareItem(candidate) : { ok: false, message: 'Kayıt seçilemedi.' });
      },
    };
  }, [compareSelection.count, compareSelection.items, compareSelection.selectedKeySet, currentListUrl]);

  const setListMode = useCallback((mode) => {
    setDraftFilters((cur) => ({ ...cur, list_mode: mode, page: 1 }));
    setAppliedFilters((cur) => ({ ...cur, list_mode: mode, page: 1 }));
  }, []);

  useEffect(() => {
    buildQueryState(appliedFilters);
  }, [appliedFilters]);

  const cityOptions = useMemo(
    () => locationSelectOptions('İl seçin', cityRows),
    [cityRows],
  );

  const townOptions = useMemo(
    () => locationSelectOptions('İlçe seçin', townsQuery.data?.towns),
    [townsQuery.data],
  );

  const quarterOptions = useMemo(
    () => locationSelectOptions('Mahalle seçin', quartersQuery.data?.quarters),
    [quartersQuery.data],
  );

  const categoryTypeOptionsQuery = useQuery({
    queryKey: ['listing-category-children', draftFilters.category_main],
    queryFn: () => fetchListingCategories(draftFilters.category_main),
    enabled: Boolean(draftFilters.category_main),
    staleTime: 300_000,
  });

  const queryTypeOptionsForDraft = useMemo(
    () => (categoryTypeOptionsQuery.data?.nodes || []).map((node) => ({
      value: String(node.id),
      label: String(node.label),
      isLeaf: Boolean(node.is_leaf),
    })),
    [categoryTypeOptionsQuery.data],
  );

  const selectedQueryTypes = useMemo(() => {
    const selectedIds = new Set([
      ...splitCsvParam(draftFilters.category_type),
      ...splitCsvParam(draftFilters.category_leaf_id),
    ]);
    return queryTypeOptionsForDraft
      .map((option) => option.value)
      .filter((value) => selectedIds.has(value));
  }, [draftFilters.category_type, draftFilters.category_leaf_id, queryTypeOptionsForDraft]);

  const metaStarsFilterValue = useMemo(
    () => starsFilterValueFromMetaRange(draftFilters.meta_min, draftFilters.meta_max),
    [draftFilters.meta_min, draftFilters.meta_max],
  );

  const setFilterValue = useCallback((field, value) => {
    setDraftFilters((current) => {
      const next = { ...current, [field]: value };
      if (field === 'city_id') {
        next.town_id = '';
        next.quarter_id = '';
      } else if (field === 'town_id') {
        next.quarter_id = '';
      } else if (field === 'query_type') {
        next.category_type = '';
        next.category_leaf_id = '';
      }
      return next;
    });
  }, []);

  const handleSortChange = useCallback((field) => {
    setAppliedFilters((current) => {
      const isSameField = current.sort_by === field;
      return {
        ...current,
        page: 1,
        sort_by: field,
        sort_dir: isSameField && current.sort_dir === 'desc' ? 'asc' : 'desc',
      };
    });
  }, []);

  const applyFilterPatch = useCallback((patch) => {
    setAppliedFilters((cur) => ({ ...cur, ...patch, page: 1 }));
    setDraftFilters((cur) => ({ ...cur, ...patch, page: 1 }));
  }, []);

  /** Kategori ağacı: taslak + sonuçları anında güncelle (Sonuçları Güncelle gerekmez). */
  const applyCategoryTreeFilters = useCallback((updater) => {
    setDraftFilters((cur) => {
      const next = typeof updater === 'function' ? updater(cur) : { ...cur, ...updater };
      const patched = { ...next, page: 1 };
      setAppliedFilters((applied) => ({
        ...applied,
        category_main: patched.category_main,
        category_type: patched.category_type,
        category_leaf_id: patched.category_leaf_id,
        query_type: patched.query_type,
        listing_attr: patched.listing_attr ?? applied.listing_attr,
        page: 1,
      }));
      return patched;
    });
  }, []);

  /** Müşteri seçimi + portal-filters yanıtı: yalnızca taslak; sonuçlar «Sonuçları Güncelle» ile uygulanır */
  const applyDraftFilterPatch = useCallback((patch) => {
    setDraftFilters((cur) => ({ ...cur, ...patch, page: 1 }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setIsApplyingFilters(true);
    setAppliedFilters({
      ...draftFilters,
      page: 1,
      page_size: appliedFilters.page_size,
      sort_by: appliedFilters.sort_by,
      sort_dir: appliedFilters.sort_dir,
    });
  }, [draftFilters, appliedFilters.page_size, appliedFilters.sort_by, appliedFilters.sort_dir]);

  const handleClearSecondaryFilters = useCallback(() => {
    setAppliedFilters((cur) => getSecondaryFiltersClearPatch(cur));
    setDraftFilters((cur) => getSecondaryFiltersClearPatch(cur));
  }, []);

  const filterChips = useMemo(() => {
    if (!hasVisibleListScope) return [];
    return buildPortalFilterChips(appliedFilters, {
      cityName: findLocationName(citiesQuery.data?.cities, appliedFilters.city_id),
      townName: findLocationName(appliedTownsForChipsQuery.data?.towns, appliedFilters.town_id),
      quarterName: findLocationName(appliedQuartersForChipsQuery.data?.quarters, appliedFilters.quarter_id),
      cityRows: citiesQuery.data?.cities,
      applyPatch: applyFilterPatch,
    });
  }, [appliedFilters, hasVisibleListScope, citiesQuery.data, appliedTownsForChipsQuery.data, appliedQuartersForChipsQuery.data, applyFilterPatch]);

  const filterSummaryLine = useMemo(() => {
    const pg = appliedFilters.page || 1;
    const ps = pageSize;
    if (listMode === LIST_MODE.QUERIES) {
      if (!hasQueryListScope) return '';
      if (!canAccessProsorgu) return isIndividualMember ? 'ProSorgu sonuçları bireysel üyelikte kapalıdır.' : 'ProSorgu sonuçları için giriş yapın veya uygun üyelikle devam edin.';
      if (listQuery.isError) return 'Sorgular yüklenirken hata oluştu.';
      if (!listQuery.isSuccess) return 'Sorgular yükleniyor…';
      const range = formatPageRangeLabel(pg, ps, totalCount);
      return `Son 90 gün sorguları: toplam ${Number(totalCount).toLocaleString('tr-TR')} kayıt (bu sayfa ${range})`;
    }
    if (listMode === LIST_MODE.LISTINGS) {
      if (!appliedFilters.city_id) return '';
      if (listingsQuery.isError) return 'İlanlar yüklenirken hata oluştu.';
      if (!listingsQuery.isSuccess) return 'İlanlar yükleniyor…';
      const range = formatPageRangeLabel(pg, ps, listingTotal);
      return `Vitrin ilanları: toplam ${Number(listingTotal).toLocaleString('tr-TR')} kayıt (bu sayfa ${range})`;
    }
    if (listMode === LIST_MODE.ALL) {
      if (!appliedFilters.city_id) return '';
      if (!canAccessVault) {
        return 'Sandık (gizli ilanlar) yalnızca danışman, kurumsal ve admin üyelere açıktır.';
      }
      if (listingsQuery.isError) return 'Sandık ilanları yüklenirken hata oluştu.';
      if (!listingsQuery.isSuccess) return 'Sandık ilanları yükleniyor…';
      const range = formatPageRangeLabel(pg, ps, listingTotal);
      return `Sandık (gizli ilanlar): toplam ${Number(listingTotal).toLocaleString('tr-TR')} kayıt (bu sayfa ${range})`;
    }
  }, [
    appliedFilters.city_id,
    appliedFilters.page,
    hasQueryListScope,
    listMode,
    pageSize,
    totalCount,
    listingTotal,
    canAccessProsorgu,
    canAccessVault,
    isIndividualMember,
    listQuery.isError,
    listQuery.isSuccess,
    listQuery.isFetched,
    listingsQuery.isError,
    listingsQuery.isSuccess,
    listingsQuery.isFetched,
  ]);

  const filterBarEl = hasVisibleListScope ? (
    <PortalListFilterCard
      summaryLine={filterSummaryLine}
      chips={filterChips}
      onClearSecondary={handleClearSecondaryFilters}
      summaryActions={<PortalCompareListLink count={compareState.count} returnTo={currentListUrl} />}
      listMode={listMode}
    />
  ) : null;
  const modeNoticeText = listMode === LIST_MODE.QUERIES
    ? 'Son 90 Gün İçinde Yapılan Pro Sorgu Sonuçları Listelenmektedir'
    : listMode === LIST_MODE.LISTINGS
      ? 'Emlak İlanları Listelenmektedir'
      : listMode === LIST_MODE.ALL
        ? 'Sandık Açıldı! Gizli İlan Portalı'
        : 'Pro Sorgu ve Emlak İlanları Listelenmektedir';
  const emptyHintText = !appliedFilters.city_id && !defaultCityResolved && !appliedFilters.consultant_user_id
    ? 'Varsayılan sonuçlar hazırlanıyor. İsterseniz il, ilçe ve mahalle filtrelerini değiştirebilirsiniz.'
    : listMode === LIST_MODE.QUERIES
      ? 'Sorguları görmek için il seçin veya kullanıcı filtresi uygulayın.'
      : 'Vitrin ilanlarını veya Sandık sonuçlarını görmek için önce il seçin.';
  const defaultCityNoticeText = defaultCityNotice
    ? defaultCityNotice.source === 'stored'
      ? `Son görüntülediğiniz il otomatik seçildi: ${defaultCityNotice.cityName}. Filtreleri değiştirebilirsiniz.`
      : `Varsayılan olarak ${defaultCityNotice.cityName} gösteriliyor. Filtreleri değiştirerek farklı konumlarda sonuç görebilirsiniz.`
    : '';

  useEffect(() => {
    const updateButtonPosition = () => {
      const panelElement = panelRef.current;
      if (!panelElement || window.innerWidth <= 960) {
        setUpdateButtonStyle(null);
        return;
      }

      const panelRect = panelElement.getBoundingClientRect();
      setUpdateButtonStyle({
        position: 'fixed',
        left: `${Math.round(panelRect.left + 10)}px`,
        width: `${Math.max(0, Math.round(panelRect.width - 20))}px`,
        bottom: '50px',
      });
    };

    updateButtonPosition();

    let frameId = 0;
    const scheduleUpdate = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        updateButtonPosition();
      });
    };

    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('scroll', scheduleUpdate, { passive: true });

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate);
    };
  }, []);

  useEffect(() => {
    if (!isApplyingFilters && !isPageTransitioning) return;
    const waitingQueries = listMode === LIST_MODE.QUERIES && canAccessProsorgu && listQuery.isFetching;
    const waitingListings = (
      listMode === LIST_MODE.LISTINGS
      || (listMode === LIST_MODE.ALL && canAccessVault)
    ) && listingsQuery.isFetching;
    if (!waitingQueries && !waitingListings) {
      setIsApplyingFilters(false);
      setIsPageTransitioning(false);
    }
  }, [
    isApplyingFilters,
    isPageTransitioning,
    listMode,
    canAccessProsorgu,
    canAccessVault,
    listQuery.isFetching,
    listingsQuery.isFetching,
  ]);

  const showListLoading = (isApplyingFilters || isPageTransitioning) && (
    (listMode === LIST_MODE.QUERIES && canAccessProsorgu && hasQueryListScope)
    || (listMode === LIST_MODE.LISTINGS && appliedFilters.city_id)
    || (listMode === LIST_MODE.ALL && canAccessVault && appliedFilters.city_id)
  );

  return (
    <div className="pp-portal-shell">
      <StickyModeNotice listMode={listMode} text={modeNoticeText} />
      <div className="pp-portal-page">
        <aside className="pp-portal-sidebar">
          <div className="pp-portal-category-tree-wrap">
            <VitrinCategorySidebar
              multiSelect
              activeMainId={draftFilters.category_main}
              activeLeafId=""
              activeBranchId=""
              selectedLeafIds={splitCsvParam(draftFilters.category_leaf_id)}
              selectedBranchIds={splitCsvParam(draftFilters.category_type)}
              onSelectMain={(id) => {
                applyCategoryTreeFilters((cur) => ({
                  ...cur,
                  category_main: id || '',
                  category_type: '',
                  category_leaf_id: '',
                  query_type: '',
                  listing_attr: '',
                }));
              }}
              onSelectLeaf={() => {}}
              onSelectBranch={() => {}}
              onToggleLeaf={(leafId, rootId) => {
                applyCategoryTreeFilters((cur) => {
                  const leaves = new Set(splitCsvParam(cur.category_leaf_id));
                  if (leaves.has(leafId)) leaves.delete(leafId);
                  else leaves.add(leafId);
                  return {
                    ...cur,
                    category_main: rootId || cur.category_main,
                    category_leaf_id: [...leaves].join(','),
                    query_type: '',
                    listing_attr: '',
                  };
                });
              }}
              onToggleBranch={(branchId, rootId) => {
                applyCategoryTreeFilters((cur) => {
                  const mids = new Set(splitCsvParam(cur.category_type));
                  if (mids.has(branchId)) mids.delete(branchId);
                  else mids.add(branchId);
                  return {
                    ...cur,
                    category_main: rootId || cur.category_main,
                    category_type: [...mids].join(','),
                    query_type: '',
                    listing_attr: '',
                  };
                });
              }}
            />
          </div>
          <div className="pp-portal-panel" ref={panelRef}>
            <div className="pp-portal-panel-title">Filtreler</div>
            <PortalListingFiltersForm
              useCollapse
              listingSearchOnly={false}
              draftFilters={draftFilters}
              setDraftFilters={setDraftFilters}
              setFilterValue={setFilterValue}
              appliedFilters={appliedFilters}
              setListMode={setListMode}
              applyDraftFilterPatch={applyDraftFilterPatch}
              cityOptions={cityOptions}
              townOptions={townOptions}
              quarterOptions={quarterOptions}
              queryTypeOptionsForDraft={queryTypeOptionsForDraft}
              selectedQueryTypes={selectedQueryTypes}
              metaStarsFilterValue={metaStarsFilterValue}
              consultantFilterEnabled
              isAuthenticated={isAuthenticated}
              consultantUsersLoading={consultantUsersQuery.isLoading}
              consultantUserOptions={consultantUserOptions}
              onConsultantFilterChange={(nextId, nextName) => {
                setDraftFilters((cur) => ({
                  ...cur,
                  consultant_user_id: nextId,
                  consultant_user_name: nextName,
                  page: 1,
                }));
                setAppliedFilters((cur) => ({
                  ...cur,
                  consultant_user_id: nextId,
                  consultant_user_name: nextName,
                  page: 1,
                }));
              }}
            />
            <div className="pp-filter-actions">
              <div className="pp-filter-actions-sticky" style={updateButtonStyle || undefined}>
                <button
                  type="button"
                  className="pp-primary-btn"
                  onClick={handleApplyFilters}
                  disabled={isApplyingFilters}
                >
                  {isApplyingFilters ? 'Sonuçlar Güncelleniyor…' : 'Sonuçları Güncelle'}
                </button>
              </div>
              <button
                type="button"
                className="pp-secondary-btn"
                onClick={() => {
                  didAutoSelectDefaultCityRef.current = false;
                  setDefaultCityNotice(null);
                  setDefaultCityResolved(false);
                  const emptyFilters = {
                    list_mode: LIST_MODE.QUERIES,
                    city_id: '',
                    town_id: '',
                    quarter_id: '',
                    query_type: '',
                    expert_answered: '',
                    price_advantage_only: '',
                    mine: false,
                    page: 1,
                    page_size: DEFAULT_PAGE_SIZE,
                    sort_by: '',
                    sort_dir: 'desc',
                    area_m2_min: '',
                    area_m2_max: '',
                    unit_price_min: '',
                    unit_price_max: '',
                    total_price_min: '',
                    total_price_max: '',
                    gm_min: '',
                    gm_max: '',
                    quarter_score_min: '',
                    quarter_score_max: '',
                    meta_min: '',
                    meta_max: '',
                    puan_min: '',
                    puan_max: '',
                    listing_has_road: '',
                    listing_has_water: '',
                    listing_has_power_line: '',
                    road_frontage_min_m: '',
                    road_frontage_max_m: '',
                    max_nav_city_m: '',
                    max_nav_town_m: '',
                    category_main: '',
                    category_type: '',
                    category_leaf_id: '',
                    listing_attr: '',
                    consultant_user_id: '',
                    consultant_user_name: '',
                  };
                  setDraftFilters(emptyFilters);
                  setAppliedFilters(emptyFilters);
                }}
              >
                Temizle
              </button>
            </div>
          </div>
        </aside>

        <main className="pp-portal-main">
          {appliedFilters.city_id && defaultCityNoticeText ? (
            <div className="pp-portal-empty-card pp-portal-default-city-banner" role="status" aria-live="polite">
              {defaultCityNoticeText}
            </div>
          ) : null}
          {hasQueryListScope && listMode === LIST_MODE.QUERIES && !canAccessProsorgu ? (
            <>
              {filterBarEl}
              <ResultsTableHeader
                sortBy={appliedFilters.sort_by}
                sortDir={appliedFilters.sort_dir}
                onSortChange={handleSortChange}
              />
              <div className="pp-portal-results-list">
                <PortalGuestProsorguInviteCard {...prosorguInviteProps} />
              </div>
            </>
          ) : null}
          {!hasVisibleListScope ? (
            <>
              <div className="pp-portal-empty-card pp-portal-empty-hint-banner">
                {emptyHintText}
              </div>
              {listMode === LIST_MODE.QUERIES ? (
                <>
                  <ResultsTableHeader
                    sortBy={appliedFilters.sort_by}
                    sortDir={appliedFilters.sort_dir}
                    onSortChange={handleSortChange}
                  />
                  <div className="pp-portal-results-list">
                    <PortalListEmptyPlaceholder kind="query" count={4} message={null} />
                  </div>
                </>
              ) : null}
              {listMode === LIST_MODE.LISTINGS ? (
                <>
                  <ListingResultsHeader
                    sortBy={appliedFilters.sort_by}
                    sortDir={appliedFilters.sort_dir}
                    onSortChange={handleSortChange}
                  />
                  <div className="pp-portal-results-list">
                    <PortalListEmptyPlaceholder kind="listing" count={4} message={null} />
                  </div>
                </>
              ) : null}
              {listMode === LIST_MODE.ALL ? (
                <>
                  <h3 className="pp-portal-list-subsection">Sandık — gizli ilanlar</h3>
                  <ListingResultsHeader
                    sortBy={appliedFilters.sort_by}
                    sortDir={appliedFilters.sort_dir}
                    onSortChange={handleSortChange}
                  />
                  <div className="pp-portal-results-list">
                    <PortalListEmptyPlaceholder kind="listing" count={4} message={null} />
                  </div>
                </>
              ) : null}
            </>
          ) : null}

          {hasQueryListScope && listMode === LIST_MODE.QUERIES && canAccessProsorgu && listQuery.isError ? (
            <div className="pp-portal-empty-card">{listQuery.error.message}</div>
          ) : null}
          {appliedFilters.city_id && listMode === LIST_MODE.LISTINGS && listingsQuery.isError ? (
            <div className="pp-portal-empty-card">{listingsQuery.error.message}</div>
          ) : null}
          {hasQueryListScope && listMode === LIST_MODE.QUERIES && canAccessProsorgu && !listQuery.isError ? (
            <PortalListBody lockMinHeight={showListLoading}>
              {showListLoading ? (
                <>
                  {filterBarEl}
                  <ResultsTableHeader
                    sortBy={appliedFilters.sort_by}
                    sortDir={appliedFilters.sort_dir}
                    onSortChange={handleSortChange}
                  />
                  <div className="pp-portal-results-list">
                    <PortalListEmptyPlaceholder
                      kind="query"
                      count={pageSize}
                      message="Sonuçlar güncelleniyor…"
                    />
                  </div>
                </>
              ) : listQuery.isSuccess ? (
                <ResultsList
                  items={items}
                  onSortChange={handleSortChange}
                  returnTo={currentListUrl}
                  sortBy={appliedFilters.sort_by}
                  sortDir={appliedFilters.sort_dir}
                  filterBar={filterBarEl}
                  compareState={compareState}
                />
              ) : (
                <>
                  {filterBarEl}
                  <ResultsTableHeader
                    sortBy={appliedFilters.sort_by}
                    sortDir={appliedFilters.sort_dir}
                    onSortChange={handleSortChange}
                  />
                  <div className="pp-portal-results-list">
                    <PortalListEmptyPlaceholder kind="query" count={4} message={null} />
                  </div>
                </>
              )}
            </PortalListBody>
          ) : null}

          {appliedFilters.city_id && listMode === LIST_MODE.LISTINGS && !listingsQuery.isError ? (
            <PortalListBody lockMinHeight={showListLoading}>
              {showListLoading ? (
                <>
                  {filterBarEl}
                  <ListingResultsHeader
                    sortBy={appliedFilters.sort_by}
                    sortDir={appliedFilters.sort_dir}
                    onSortChange={handleSortChange}
                  />
                  <div className="pp-portal-results-list">
                    <PortalListEmptyPlaceholder
                      kind="listing"
                      count={pageSize}
                      message="Sonuçlar güncelleniyor…"
                    />
                  </div>
                </>
              ) : listingsQuery.isSuccess ? (
                <ListingResultsList
                  items={listingItems}
                  returnTo={currentListUrl}
                  sortBy={appliedFilters.sort_by}
                  sortDir={appliedFilters.sort_dir}
                  onSortChange={handleSortChange}
                  filterBar={filterBarEl}
                  compareState={compareState}
                />
              ) : (
                <>
                  {filterBarEl}
                  <ListingResultsHeader
                    sortBy={appliedFilters.sort_by}
                    sortDir={appliedFilters.sort_dir}
                    onSortChange={handleSortChange}
                  />
                  <div className="pp-portal-results-list">
                    <PortalListEmptyPlaceholder kind="listing" count={4} message={null} />
                  </div>
                </>
              )}
            </PortalListBody>
          ) : null}

          {appliedFilters.city_id && listMode === LIST_MODE.ALL ? (
            <>
              <h3 className="pp-portal-list-subsection">Sandık — gizli ilanlar</h3>
              {!canAccessVault ? (
                <>
                  {filterBarEl}
                  <ListingResultsHeader
                    sortBy={appliedFilters.sort_by}
                    sortDir={appliedFilters.sort_dir}
                    onSortChange={handleSortChange}
                  />
                  <div className="pp-portal-results-list">
                    <PortalGuestProsorguInviteCard {...vaultInviteProps} />
                  </div>
                </>
              ) : listingsQuery.isError ? (
                <div className="pp-portal-empty-card">{listingsQuery.error.message}</div>
              ) : (
                <PortalListBody lockMinHeight={showListLoading}>
                  {showListLoading ? (
                    <>
                      {filterBarEl}
                      <ListingResultsHeader
                        sortBy={appliedFilters.sort_by}
                        sortDir={appliedFilters.sort_dir}
                        onSortChange={handleSortChange}
                      />
                      <div className="pp-portal-results-list">
                        <PortalListEmptyPlaceholder
                          kind="listing"
                          count={pageSize}
                          message="Sonuçlar güncelleniyor…"
                        />
                      </div>
                    </>
                  ) : listingsQuery.isSuccess ? (
                    <ListingResultsList
                      items={listingItems}
                      returnTo={currentListUrl}
                      sortBy={appliedFilters.sort_by}
                      sortDir={appliedFilters.sort_dir}
                      onSortChange={handleSortChange}
                      filterBar={filterBarEl}
                      compareState={compareState}
                    />
                  ) : (
                    <>
                      {filterBarEl}
                      <ListingResultsHeader
                        sortBy={appliedFilters.sort_by}
                        sortDir={appliedFilters.sort_dir}
                        onSortChange={handleSortChange}
                      />
                      <div className="pp-portal-results-list">
                        <PortalListEmptyPlaceholder kind="listing" count={4} message={null} />
                      </div>
                    </>
                  )}
                </PortalListBody>
              )}
            </>
          ) : null}

          {(
            (listMode === LIST_MODE.QUERIES && hasQueryListScope && canAccessProsorgu && (listQuery.isSuccess || showListLoading))
            || (listMode === LIST_MODE.LISTINGS && appliedFilters.city_id && (listingsQuery.isSuccess || showListLoading))
            || (listMode === LIST_MODE.ALL && appliedFilters.city_id && canAccessVault && (listingsQuery.isSuccess || showListLoading))
          ) ? (
            <div className="pp-portal-list-footer">
              <div className="pp-portal-pagination-summary" aria-live="polite">
                {listMode === LIST_MODE.QUERIES && canAccessProsorgu ? (
                  <span className="pp-portal-pagination-total">
                    Son sorgular: toplam <strong>{totalCount}</strong> kayıt
                    {totalCount > 0 && pageSize > 0 ? (
                      <>
                        {' '}
                        (bu sayfa{' '}
                        <strong>
                          {(appliedFilters.page - 1) * pageSize + 1}
                          –
                          {Math.min(appliedFilters.page * pageSize, totalCount)}
                        </strong>
                        )
                      </>
                    ) : null}
                  </span>
                ) : null}
                {listMode === LIST_MODE.LISTINGS ? (
                  <span className="pp-portal-pagination-total">
                    Vitrin ilanları: toplam <strong>{listingTotal}</strong> kayıt
                  </span>
                ) : null}
                {listMode === LIST_MODE.ALL && canAccessVault ? (
                  <span className="pp-portal-pagination-total">
                    Sandık (gizli ilanlar): toplam <strong>{listingTotal}</strong> kayıt
                  </span>
                ) : null}
              </div>
              <div className="pp-portal-pagination-left">
                <label htmlFor="pp-page-size">Sayfa başına</label>
                <select
                  id="pp-page-size"
                  value={String(appliedFilters.page_size)}
                  onChange={(event) => {
                    const nextSize = Number(event.currentTarget.value) || DEFAULT_PAGE_SIZE;
                    setIsPageTransitioning(true);
                    setAppliedFilters((current) => ({
                      ...current,
                      page: 1,
                      page_size: nextSize,
                    }));
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <div className="pp-portal-pagination-right">
                <button
                  type="button"
                  className="pp-pagination-btn"
                  disabled={appliedFilters.page <= 1 || showListLoading}
                  onClick={() => {
                    setIsPageTransitioning(true);
                    setAppliedFilters((current) => ({ ...current, page: Math.max(1, current.page - 1) }));
                  }}
                  aria-label="Önceki sayfa"
                >
                  <i className="fas fa-chevron-left" />
                </button>
                <span className="pp-page-info">{appliedFilters.page} / {navTotalPages}</span>
                <button
                  type="button"
                  className="pp-pagination-btn"
                  disabled={nextPageDisabled || showListLoading}
                  onClick={() => {
                    setIsPageTransitioning(true);
                    setAppliedFilters((current) => ({
                      ...current,
                      page: Math.min(navTotalPages, current.page + 1),
                    }));
                  }}
                  aria-label="Sonraki sayfa"
                >
                  <i className="fas fa-chevron-right" />
                </button>
              </div>
            </div>
          ) : null}
        </main>
      </div>

      {activeRatingSnapshot ? (
        <Suspense fallback={null}>
          <RatingsDrawer
            snapshotId={activeRatingSnapshot}
            onClose={() => setActiveRatingSnapshot(null)}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
